import { useEffect } from 'react';
import Sheet from 'react-modal-sheet';
import SheetClose from './SheetClose';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SEARCH_PARAMS } from '../constants/searchParamNames';
import LoadingIndicator from './LoadingIndicator';
import OfferOverlayContent from './OfferOverlayContent';
import { DisplayedIn, SingleOffer, YSH_GTM_EVENTS } from '../types/types';
import { QUERY_KEYS } from '../constants/queryKeys';
import { callPublicAPI } from '../utils/api';
import { getPageNameFromLocation, mapSingleOfferToAnalyticsObject, pushToAnalytics } from '../utils/analytics';
import '../scss/offer-overlay.scss';

const OfferOverlay = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const offerId = searchParams.get(SEARCH_PARAMS.OFFER_ID) ?? '';

  const { isLoading, data: fetchedOffer } = useQuery<SingleOffer>({
    queryKey: [QUERY_KEYS.GET_OFFER, offerId],
    queryFn: async () => {
      const response = await callPublicAPI<SingleOffer>('GET', `/offers/${offerId}`);
      return response.data;
    },
    enabled: Boolean(offerId)
  });

  const closeModal = () => {
    searchParams.delete(SEARCH_PARAMS.OFFER_ID);
    searchParams.delete(SEARCH_PARAMS.OPENED_FROM);
    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (fetchedOffer) {
      const openedFrom = searchParams.get(SEARCH_PARAMS.OPENED_FROM) ?? 'Coupon details';

      pushToAnalytics({
        eventName: YSH_GTM_EVENTS.COUPON_DETAILS,
        data: {
          coupon_details: [mapSingleOfferToAnalyticsObject(fetchedOffer, openedFrom as DisplayedIn)]
        },
        placement: openedFrom ? `${getPageNameFromLocation(window.location.pathname)} - ${openedFrom}` : undefined
      });
    }
  }, [fetchedOffer]);

  return (
    <>
      <Sheet isOpen={Boolean(offerId)} onClose={closeModal} className="offer-overlay">
        <Sheet.Container>
          <Sheet.Header />
          <Sheet.Content>
            <>
              <SheetClose closeModal={closeModal} />
              <Sheet.Scroller>
                {fetchedOffer && (
                  <>
                    {isLoading && <LoadingIndicator />}
                    <OfferOverlayContent offer={fetchedOffer} />
                  </>
                )}
              </Sheet.Scroller>
            </>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={closeModal} />
      </Sheet>
    </>
  );
};

export default OfferOverlay;
